// Figgo – das Maskottchen der Pille (Handoff Pille §9). Reine Szenen-Logik: Ereignis + Kontext
// (Uhrzeit, Stempelstatus, Minuten, Geometrie der Pille) → Schrittliste. Zufall wird injiziert,
// damit die Auswahl testbar bleibt. Alle Texte wörtlich aus dem Prototyp (bjScene).

export type FiggoPose =
  | 'stand' | 'step' | 'run' | 'wave' | 'look' | 'stretch' | 'coffee' | 'juggle' | 'dance' | 'trip' | 'sleep'
  | 'perch' | 'hang' | 'phone' | 'party' | 'shock' | 'facepalm' | 'carry' | 'lift' | 'magnify' | 'read'
  | 'shrug' | 'sweep' | 'point' | 'throw';

export interface FiggoSchritt {
  x: number;
  y: number;
  pose: FiggoPose;
  /** feste Dauer der Bewegung; sonst aus Weg und Tempo */
  ms?: number;
  /** Verweilzeit (wird wie im Prototyp gestreckt, siehe verweilMs) */
  hold?: number;
  say?: string;
  /** px/s */
  speed?: number;
}

export interface FiggoSzene {
  /** Startpunkt außerhalb, falls Figgo noch nicht da ist */
  spawn?: { x: number; y: number; pose: FiggoPose };
  steps: FiggoSchritt[];
}

export type FiggoEreignis =
  | 'visit' | 'zeitStatus' | 'success' | 'error' | 'live' | 'anruf' | 'poke'
  | 'page:search' | 'typing' | 'nohits' | 'page:widgets' | 'page:notifications' | 'sweep' | 'page:zeit'
  | 'kommen' | 'pause' | 'weiter' | 'gehen' | 'meilenstein'
  | 'page:chat' | 'send' | 'page:telefon' | 'tel' | 'page:controls' | 'form';

export interface FiggoAnker { x: number; y: number; w: number; top: number }

export interface FiggoGeometrie {
  /** Maßstab (offen 1.25) */
  s: number;
  /** Breite der Pille */
  W: number;
  /** y der Standfläche */
  floor: number;
  anker: (name: 'search' | 'widget' | 'zeit' | 'chat') => FiggoAnker | null;
  /** aktuelle Position, falls Figgo schon da ist */
  aktuell: { x: number; y: number } | null;
}

export type ZeitStatus = 'aus' | 'pause' | 'arbeit' | 'feierabend';

export interface ZeitLage {
  st: ZeitStatus;
  /** Netto-Minuten heute */
  min: number;
  /** Minuten der laufenden Pause */
  pMin: number;
  /** heute schon Pause gemacht? */
  pauseTaken: boolean;
}

export interface FiggoKontext {
  stunde: number;
  /** 0 = Sonntag … 5 = Freitag */
  wochentag: number;
  zeit: ZeitLage;
  /** Name des Bereichs für „Was erledigen wir in …?“ */
  bereich: string | null;
  ungelesen: number;
  /** Meilenstein-Minuten (meilenstein) bzw. Tagesminuten (gehen) */
  m?: number;
  /** Offenes Pillen-Formular hat Pflichtfelder? false = keine (dann kein Pflichtfeld-Spruch), undefined = unbekannt. */
  formPflicht?: boolean;
}

export type Zufall = () => number;

/** „6:22 h“ – abgerundet (Prototyp: Z.f) */
export function fMin(m: number): string {
  return `${Math.floor(m / 60)}:${String(Math.floor(m % 60)).padStart(2, '0')} h`;
}

/** „8:15 h“ – gerundet (Prototyp: fmtMin) */
export function fmtMin(m: number): string {
  const r = Math.round(m);
  return `${Math.floor(r / 60)}:${String(r % 60).padStart(2, '0')} h`;
}

/** Stempelstatus aus echten Daten. */
export function zeitLage(p: {
  laufend: boolean;
  inPause: boolean;
  heuteMinuten: number;
  /** Beginn der offenen Pause (ISO) */
  pauseSeit: string | null;
  /** Anzahl Pausen heute (abgeschlossen oder offen) */
  pausenHeute: number;
  /** gab es heute schon Stempel? */
  heuteGestempelt: boolean;
  jetzt: Date;
}): ZeitLage {
  const pMin = p.inPause && p.pauseSeit ? Math.max(0, (p.jetzt.getTime() - new Date(p.pauseSeit).getTime()) / 60000) : 0;
  const st: ZeitStatus = !p.laufend ? (p.heuteGestempelt ? 'feierabend' : 'aus') : p.inPause ? 'pause' : 'arbeit';
  return { st, min: p.heuteMinuten, pMin, pauseTaken: p.pausenHeute > 0 };
}

const MEILENSTEINE = [360, 480, 540, 600] as const;

/** Meilensteine (6/8/9/10 h), die jetzt fällig sind: erreicht, noch nicht gespielt, höchstens 2 Min. her. */
export function faelligeMeilensteine(min: number, gespielt: ReadonlySet<number>): number[] {
  return MEILENSTEINE.filter(t => min >= t && !gespielt.has(t) && min - t < 2);
}

/** Alle erreichten Meilensteine – werden als gespielt markiert, auch wenn die Szene nicht lief. */
export function erreichteMeilensteine(min: number): number[] {
  return MEILENSTEINE.filter(t => min >= t);
}

/** Häufigkeitsregeln von bjPlay. */
export function darfSpielen(p: {
  reduziert: boolean;
  aus: boolean;
  force: boolean;
  laeuft: boolean;
  jetzt: number;
  letzterStart: number;
  cool?: number;
  wahrscheinlichkeit?: number;
  zufall: Zufall;
}): boolean {
  if (p.reduziert || p.aus) return false;
  if (!p.force && p.laeuft) return false;
  if (!p.force && p.jetzt - p.letzterStart < (p.cool ?? 9000)) return false;
  return p.zufall() <= (p.wahrscheinlichkeit ?? 1);
}

/** Dauer einer Bewegung (Prototyp: 62 px/s geschlossen, 95 offen, mind. 220 ms bei Weg). */
export function bewegungMs(dist: number, schritt: Pick<FiggoSchritt, 'ms' | 'speed'>, offen: boolean): number {
  if (schritt.ms != null) return schritt.ms;
  return Math.max(dist ? 220 : 0, (dist / (schritt.speed ?? (offen ? 95 : 62))) * 1000);
}

/** Verweilen nach einem Schritt: hold × 1,6 + 1 s; vor dem Hinausgehen 900 ms. */
export function verweilMs(hold: number | undefined, verlaesst: boolean, index: number): number {
  if (hold) return Math.round(hold * 1.6 + 1000);
  return verlaesst && index > 0 ? 900 : 0;
}

interface Akt { p: FiggoPose; h: number; s?: string; y?: number; ms?: number }

/** Szene zu einem Ereignis – null, wenn sie hier nicht passt (z. B. Anker fehlt, nachts kein Stempel-Hinweis). */
export function figgoSzene(kind: FiggoEreignis, g: FiggoGeometrie, k: FiggoKontext, zufall: Zufall): FiggoSzene | null {
  const { s, W, floor: fl } = g;
  const offL = -36 * s - 8;
  const offR = W + 8;
  const cur = g.aktuell;
  const hr = k.stunde;
  const night = hr >= 20 || hr < 6;
  const pick = <T,>(a: readonly T[]): T => a[Math.min(a.length - 1, Math.floor(zufall() * a.length))]!;
  const out = (x: number, y: number, pose: FiggoPose = 'step'): FiggoSchritt => ({ x: x < W / 2 ? offL : offR, y, pose });
  const at = (x: number, y: number, pose: FiggoPose, hold: number, say?: string, ms?: number): FiggoSchritt =>
    ({ x, y, pose, ms: ms ?? 0, hold, say });
  const visitFrom = (side: 'L' | 'R', x: number, y: number, acts: Akt[], exitPose: FiggoPose = 'step'): FiggoSzene => ({
    spawn: { x: side === 'L' ? offL : offR, y, pose: 'step' },
    steps: [
      { x, y, pose: 'step' },
      ...acts.map(a => at(x, a.y ?? y, a.p, a.h, a.s, a.ms)),
      { x: side === 'L' ? offL : offR, y, pose: exitPose },
    ],
  });

  switch (kind) {
    case 'visit': {
      if (!night && zufall() < 0.7) {
        const zs = figgoSzene('zeitStatus', g, k, zufall);
        if (zs) return zs;
      }
      const greet = night ? 'Gähn … noch wach?' : hr < 11 ? 'Moin!' : hr < 14 ? 'Mahlzeit!' : hr < 17 ? 'Hallo!' : 'Na, bald Feierabend?';
      const fun: Akt[] = night
        ? [{ p: 'sleep', h: 4200, s: 'Nur kurz die Augen zu …' }, { p: 'shock', h: 900, s: 'Ich war wach! Ehrlich!' }]
        : [pick<Akt>([
          { p: 'juggle', h: 3200, s: 'Guck mal, was ich kann!' },
          { p: 'coffee', h: 3400, s: 'Kaffee. Lebenselixier.' },
          { p: 'dance', h: 2800, s: pick(['Kleine Tanzpause!', 'Ich hab Rhythmus!']) },
          { p: 'trip', h: 2500, s: 'Uff! Wer hat das da hingestellt?' },
          { p: 'stretch', h: 2600, s: 'Langer Tag heute …' },
          { p: 'look', h: 2600, s: 'Alles ruhig hier?' },
        ])];
      return visitFrom('L', 8, fl, [{ p: 'wave', h: 1500, s: greet }, ...fun]);
    }
    case 'zeitStatus': {
      const Z = k.zeit;
      const X = 8;
      if (Z.st === 'aus') {
        return hr >= 6 && hr < 17
          ? visitFrom('L', X, fl, [{ p: 'look', h: 1600, s: 'Hallo? Jemand da?' }, { p: 'point', h: 2600, s: pick(['Noch nicht eingestempelt?', 'Die Stempeluhr wartet!', 'Kommen nicht vergessen!']) }])
          : null;
      }
      if (Z.st === 'pause') {
        return Z.pMin >= 30
          ? visitFrom('L', X, fl, [{ p: 'stretch', h: 2400, s: `Pause ist ${Math.floor(Z.pMin)} Min. lang.` }, { p: 'point', h: 2000, s: 'Weiter geht’s?' }])
          : visitFrom('L', X, fl, [pick<Akt>([
            { p: 'coffee', h: 3400, s: pick(['Noch ein Schluck …', 'Pause ist heilig.', 'Kaffee Nummer zwei.']) },
            { p: 'sleep', h: 3600, s: 'Powernap. Nicht stören.' },
            { p: 'read', h: 3000, s: 'Ich les kurz die Zeitung.' },
          ])]);
      }
      if (Z.st === 'feierabend') {
        return visitFrom('L', X, fl, [pick<Akt>([
          { p: 'wave', h: 2200, s: 'Feierabend ist Feierabend!' },
          { p: 'sleep', h: 3600, s: 'Ich hab auch frei …' },
          { p: 'dance', h: 2600, s: k.wochentag === 5 ? 'Wochenende kann kommen!' : 'Freizeit! Endlich.' },
        ])]);
      }
      if (Z.min >= 600) return visitFrom('L', X, fl, [{ p: 'shock', h: 1000, s: '10 Stunden!' }, { p: 'point', h: 2600, s: 'Mehr erlaubt das Gesetz nicht. Ab nach Hause!' }]);
      if (Z.min >= 540) return visitFrom('L', X, fl, [{ p: 'stretch', h: 2400, s: `Schon ${fMin(Z.min)}. Bald Schluss?` }]);
      if (Z.min >= 480) return visitFrom('L', X, fl, [{ p: 'juggle', h: 2600, s: 'Soll geschafft! Alles ab jetzt ist Bonus.' }]);
      if (Z.min >= 345 && !Z.pauseTaken) return visitFrom('L', X, fl, [{ p: 'coffee', h: 2800, s: 'Fast 6 Stunden. Zeit für 30 Min. Pause!' }]);
      return visitFrom('L', X, fl, [pick<Akt>([
        { p: 'read', h: 3000, s: pick(['Akten wälzen …', 'Ich arbeite mit!', 'Viel zu tun heute.']) },
        { p: 'phone', h: 2800, s: pick(['Ja, Hausverwaltung Schwede?', 'Moment, ich schau nach …']) },
        { p: 'carry', h: 0, s: 'Ordner Nummer 17 …' },
        { p: 'look', h: 2200, s: `${fMin(Z.min)} heute. Läuft!` },
      ])]);
    }
    case 'success':
      return { spawn: { x: offL, y: fl, pose: 'run' }, steps: [{ x: 8, y: fl, pose: 'run', speed: 170 }, at(8, fl, 'party', 2100), { x: offL, y: fl, pose: 'run', speed: 170 }] };
    case 'error':
      return { spawn: { x: offL, y: fl, pose: 'run' }, steps: [{ x: 8, y: fl, pose: 'run', speed: 170 }, at(8, fl, 'shock', 900), at(8, fl, 'facepalm', 1500), out(8, fl)] };
    case 'live':
      return { spawn: { x: offL, y: fl, pose: 'carry' }, steps: [{ x: 8, y: fl, pose: 'carry', speed: 45 }, at(8, fl, 'lift', 600000)] };
    case 'anruf':
      return visitFrom('R', W - 104, 6, [{ p: 'phone', h: 3200 }]);
    case 'poke': {
      if (!cur) return null;
      const f = pick<[FiggoPose, number, string]>([
        ['trip', 2500, 'Hey! Nicht schubsen!'], ['juggle', 2400, 'Ta-da!'], ['dance', 2200, 'Ich hab Rhythmus!'],
        ['shock', 1000, 'Huch! Erschreck mich nicht!'], ['shrug', 1600, 'Was denn?'],
      ]);
      return { steps: [at(cur.x, cur.y, f[0], f[1], f[2]), { ...out(cur.x, cur.y, 'run'), speed: 170 }] };
    }
    case 'page:search': {
      const a = g.anker('search');
      if (!a) return null;
      return visitFrom('R', a.x + a.w - 84 * s, a.top, [{ p: 'magnify', h: 2400, s: pick(['Wonach suchen wir heute?', 'Ich bin ganz Auge.']) }, { p: 'perch', h: 2600 }]);
    }
    case 'typing':
    case 'nohits': {
      const a = g.anker('search');
      if (!a) return null;
      const x = a.x + a.w - 84 * s;
      const pose: FiggoPose = kind === 'typing' ? 'read' : 'shrug';
      const hold = kind === 'typing' ? 1800 : 2000;
      if (cur) {
        const msg = kind === 'typing' ? pick(['Ich lese mit …', 'Spannend!', 'Gleich hab ich’s!']) : pick(['Nichts gefunden. Tippfehler?', 'Hm, hier ist nix.', 'Vielleicht anders schreiben?']);
        return { steps: [{ x, y: a.top, pose: 'step' }, at(x, a.top, pose, hold, msg), out(x, a.top)] };
      }
      const msg = kind === 'typing' ? pick(['Ich lese mit …', 'Spannend!']) : pick(['Nichts gefunden. Tippfehler?', 'Hm, hier ist nix.', 'Vielleicht anders schreiben?']);
      return visitFrom('R', x, a.top, [{ p: pose, h: hold, s: msg }]);
    }
    case 'page:widgets': {
      const a = g.anker('widget');
      if (!a) return null;
      const x = a.x + a.w * 0.42;
      const v = pick(['perch', 'hang', 'sleep'] as const);
      const acts: Akt[] = v === 'perch'
        ? [{ p: 'perch', h: 3200, s: 'Schöne Aussicht hier oben!' }]
        : v === 'hang'
          ? [{ p: 'hang', y: a.y - 7.5 * s, ms: 260, h: 2800, s: 'Hilfe! Zieh mich hoch!' }, { p: 'stand', y: a.top, ms: 260, h: 900, s: 'Puh. Geschafft.' }]
          : [{ p: 'sleep', h: 3200, s: 'Bequemes Widget.' }];
      return visitFrom('L', x, a.top, acts);
    }
    case 'page:notifications':
      return visitFrom('R', W * 0.62, fl, k.ungelesen
        ? [{ p: 'read', h: 2400, s: `${k.ungelesen} Meldungen? Ich hol Kaffee.` }, { p: 'coffee', h: 1800 }]
        : [{ p: 'wave', h: 2000, s: 'Alles erledigt. Stark!' }]);
    case 'sweep':
      return { spawn: { x: offL, y: fl, pose: 'sweep' }, steps: [{ x: W * 0.45, y: fl, pose: 'sweep', speed: 70, say: 'Alles sauber!' }, at(W * 0.45, fl, 'sweep', 900, 'Alles sauber!'), { x: offR, y: fl, pose: 'sweep', speed: 70 }] };
    case 'page:zeit': {
      const a = g.anker('zeit');
      const y = a ? a.top : fl;
      const x = a ? a.x + a.w - 70 * s : W * 0.6;
      return visitFrom('R', x, y, [{ p: 'point', h: 2200, s: k.zeit.st === 'arbeit' || k.zeit.st === 'pause' ? 'Die Uhr läuft. Weiter so!' : 'Noch nicht eingestempelt?' }]);
    }
    case 'kommen': {
      const g2 = hr < 7 ? 'Früher Vogel! Respekt.' : hr < 9 ? 'Guten Morgen! Pünktlich wie immer.' : hr < 11 ? 'Na, ausgeschlafen? Auf geht’s!' : hr < 14 ? 'Mahlzeit! Schön, dass du da bist.' : 'Spätschicht? Los geht’s!';
      return { spawn: { x: offL, y: fl, pose: 'run' }, steps: [{ x: W * 0.3, y: fl, pose: 'run', speed: 170 }, at(W * 0.3, fl, 'wave', 1800, g2), { x: offR, y: fl, pose: 'step' }] };
    }
    case 'pause':
      return visitFrom('R', W * 0.6, fl, [{ p: 'coffee', h: 3200, s: 'Kaffeepause! Endlich.' }]);
    case 'weiter':
      return visitFrom('L', W * 0.4, fl, [{ p: 'stretch', h: 1600, s: pick(['Frisch gestärkt!', 'So, weiter im Text.', 'Akku wieder voll!']) }], 'run');
    case 'meilenstein': {
      const m = k.m ?? 0;
      const S: Akt[] = m === 480 ? [{ p: 'juggle', h: 2800, s: '8 Stunden. Soll geschafft!' }]
        : m === 360 ? [{ p: 'coffee', h: 3000, s: '6 Stunden durch. Pause nicht vergessen!' }]
          : m === 600 ? [{ p: 'shock', h: 1000, s: '10 Stunden!' }, { p: 'point', h: 2600, s: 'Höchstarbeitszeit erreicht. Gehen!' }]
            : [{ p: 'stretch', h: 2400, s: `${m / 60} Stunden geschafft.` }];
      return visitFrom('L', 8, fl, S);
    }
    case 'gehen': {
      const m = k.m ?? 0;
      const ot = m - 480;
      const t = m ? (ot > 5 ? `${fmtMin(m)} heute, ${fmtMin(ot)} drüber. Ab nach Hause!` : ot < -30 ? 'Heute kürzer? Gönn dir!' : `${fmtMin(m)} heute. Feierabend!`) : 'Feierabend! Bis morgen.';
      return visitFrom('L', W * 0.4, fl, [{ p: 'wave', h: 2400, s: t }, { p: 'dance', h: 1600, s: k.wochentag === 5 ? 'Schönes Wochenende!' : 'Bis morgen!' }], 'run');
    }
    case 'page:chat':
    case 'send': {
      const a = g.anker('chat');
      const y = a ? a.top : fl;
      const x = a ? a.x + a.w * 0.55 : W * 0.5;
      if (kind === 'page:chat') return visitFrom('R', x, y, [{ p: 'look', h: 1900, s: 'Ich les nicht mit. Ehrlich.' }]);
      if (cur) return { steps: [{ x, y, pose: 'step' }, at(x, y, 'throw', 1500, 'Abgeschickt!'), out(x, y)] };
      return visitFrom('R', x, y, [{ p: 'throw', h: 1500, s: 'Abgeschickt!' }]);
    }
    case 'page:telefon':
      return visitFrom('L', W * 0.35, fl, [{ p: 'phone', h: 2400, s: 'Wen rufen wir an?' }]);
    case 'tel':
      return visitFrom('L', W * 0.35, fl, [{ p: 'phone', h: 2000, s: pick(['Tüt … tüt …', 'Ich wähl schon mal mit.']) }]);
    case 'page:controls':
      return visitFrom('L', W * 0.4, fl, [{ p: 'point', h: 2200, s: `Was erledigen wir in ${k.bereich || 'diesem Bereich'}?` }]);
    case 'form': {
      // „Pflichtfelder nicht vergessen!“ nur, wenn das Formular welche hat (bzw. es unbekannt ist).
      const saetze = k.formPflicht === false
        ? ['Ich helf beim Ausfüllen.', 'Sieht wichtig aus.']
        : ['Ich helf beim Ausfüllen.', 'Pflichtfelder nicht vergessen!', 'Sieht wichtig aus.'];
      return visitFrom('R', W - 120 * s, fl, [{ p: 'read', h: 2600, s: pick(saetze) }]);
    }
    default:
      return null;
  }
}
